import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { Dialog, DialogContent, DialogActions, Toolbar } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import FilePreviewDialog from './FilePreviewDialog';
import { useLocation, useHistory } from 'react-router';

import { confirmPostFile, FileUploads, getUserFiles, renameFile, tempDeleteFile } from './FileAPI';
import { toastError } from '../../utils/Functions';

import FilePondWrapper from './FilePondWrapper';
import FileList from './FileList';
import Axios from 'axios';
import { getSource } from '../../utils/AccountsAPI';

function TabPanel(props: any) {
  const { children, value, index, keepMounted, ...other } = props;

  return (
    <Typography component='div' role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index || keepMounted ? (
        <Box
          p={3}
          style={{
            padding: '8px',
            paddingTop: '12px',
            width: '100%',
            height: '100%',
            display: value === index ? '' : 'none',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </Box>
      ) : null}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    transform: 'translate3d(0,0,0)',
    boxSizing: 'border-box',
  },
  primary: {
    backgroundColor: '#1976D2',
  },
  secondary: {
    backgroundColor: 'white',
  },
  undoSpan: {
    color: 'black',
    fontWeight: 'bold',
    textDecoration: 'underline',
    float: 'right',
  },
  minHeightDialog: {
    minHeight: '600px',
    height: '600px',
    boxSizing: 'border-box',
  },
  tabs: {
    color: 'white',
  },
  tabPanel: {
    width: '100%',
    height: 'calc(100% - 64px)',
  },
}));

export default function FileDialog(props) {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });

  const [tabIndex, setTabIndex] = React.useState(0);
  const [files, setFiles] = React.useState<FileUploads[]>(props.files ?? []);
  const [selectedFile, setSelectedFile] = React.useState(props?.selectedFile?.id ?? {});
  const [deleteMode, setDeleteMode] = React.useState(false);
  const [deletedFiles, setDeletedFiles] = React.useState<any[]>([]);

  const propsSelectedFileID = props?.selectedFile?.id;

  const location = useLocation();
  const history = useHistory();

  const locationState: any = location.state;
  const previewFileId = locationState?.['previewFileDialogId' + (props.id ?? '')];

  const userId = props.selectedUserID ?? props.employeeInfo?.id;

  //API calls
  useEffect(() => {
    var source = getSource();
    if (userId)
      getUserFiles(userId, source.token)
        .then((files) =>
          setFiles((oldFiles) => {
            if (files)
              return files.sort((a, b) => {
                return (a.createdDate ?? 0) < (b.createdDate ?? 0) ? 1 : -1;
              });
            else return [];
          })
        )
        .catch((err) => {
          if (Axios.isCancel(err)) return;
          toastError(props.toast, err);
        });
    return () => source.cancel();
  }, [userId, props.toast]);

  useEffect(() => {
    setSelectedFile(propsSelectedFileID);
  }, [propsSelectedFileID]);

  const handleTabChange = (event, newTabIndex) => {
    setTabIndex(newTabIndex);
    setDeleteMode(false);
    setDeletedFiles([]);
  };

  const addFile = (newFile, setSelected = true) => {
    var fileIndex = files.findIndex((file) => file.id === newFile.id);
    if (fileIndex === -1) {
      setFiles((oldFiles) => [newFile, ...oldFiles]);
      if (setSelected) setSelectedFile(newFile.id);
    }
  };

  const removeFile = (id, addToDeleted = true) => {
    var fileIndex = files.findIndex((file) => file.id === id);
    if (fileIndex >= 0) {
      var removedFiles = JSON.parse(JSON.stringify(files.splice(fileIndex, 1)));
      setFiles([...files]);
      if (addToDeleted) setDeletedFiles((oldFiles) => [...oldFiles, ...removedFiles]);
    }
  };

  const updateFile = (editedFile) => {
    if (editedFile.fileNickname) {
      renameFile(editedFile.uniqueId, editedFile.fileNickname)
        .then((response) => {
          var editedFileIndex = files.findIndex((file) => file.id === response.id);
          if (editedFileIndex !== -1) {
            files.splice(editedFileIndex, 1, response);
            setFiles([...files]);
          }
        })
        .catch((err) => {
          toastError(props.toast, err);
        });
    }
  };

  const undoDelete = () => {
    props.toast.dismiss();
    deletedFiles.forEach((file) => revertDeletedFile(file));
    setDeletedFiles([]);
    props.toast.success('Files un-deleted.', {
      position: 'bottom-left',
      autoClose: 2000,
      hideProgressBar: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const revertDeletedFile = (file) => {
    confirmPostFile(file.uniqueId)
      .then((response) => {
        addFile(file, false);
      })
      .catch((err) => {
        toastError(props.toast, err);
      });
  };

  const deleteFile = (file) => {
    return tempDeleteFile(file.uniqueId)
      .then((tempFile) => {
        if (tempFile) removeFile(tempFile.id);
        else removeFile(file.id, false);
        props.toast.success('File Deleted.', {
          position: 'bottom-left',
          autoClose: 5000,
          hideProgressBar: false,
          pauseOnHover: true,
          draggable: true,
        });
      })
      .catch((err) => {
        toastError(props.toast, err);
      });
  };

  const finishDelete = () => {
    var promises: any[] = [];
    deletedFiles.forEach((file) => {
      promises.push(deleteFile(file));
    });

    Promise.all(promises)
      .then((responses) => {
        setDeletedFiles([]);
      })
      .catch((errors) => {
        errors.forEach((err) => toastError(props.toast, err));
      });

    setDeleteMode(false);
  };

  const handleCancel = (e, reason?) => {
    if (reason === 'backdropClick') {
      return;
    } else {
      cleanup();
      if (props.onClose) {
        props.onClose();
      }
    }
  };

  const handleSave = () => {
    cleanup();
    if (props.fileToReturn) {
      var file = files.find((file) => file.id === selectedFile);
      props.fileToReturn(file);
    }
    if (props.onClose) {
      props.onClose();
    }
  };

  const cleanup = () => {
    files.forEach((file) => {
      if (file.status === 'TEMP') confirmPostFile(file.uniqueId).catch((err) => toastError(props.toast, err));
    });
    deletedFiles.forEach((file) => {
      deleteFile(file.uniqueId).catch((err) => toastError(props.toast, err));
    });
  };

  const previewFile = (id) => {
    history.push(location.pathname, { ...(location.state as any), ['previewFileDialogId' + (props.id ?? '')]: id });
  };

  return (
    <>
      <Dialog
        className={classes.root}
        open={props.open ?? true}
        fullScreen={fullScreen}
        maxWidth={'md'}
        fullWidth
        onClose={handleCancel}
        classes={{ paper: fullScreen ? '' : classes.minHeightDialog }}
      >
        <DialogContent style={{ padding: 0 }}>
          <AppBar position='sticky' classes={{ colorPrimary: classes.primary, colorSecondary: classes.secondary }}>
            <Toolbar>
              <Tabs
                className={classes.tabs}
                value={tabIndex}
                onChange={handleTabChange}
                variant={'standard'}
                aria-label='file-dialog-tabs'
                classes={{ indicator: classes.secondary }}
              >
                <Tab label='Upload' />
                <Tab label='Unassigned' />
                <Tab label='All Files' />
              </Tabs>
            </Toolbar>
          </AppBar>
          <TabPanel value={tabIndex} index={0} className={classes.tabPanel}>
            <FilePondWrapper toast={props.toast} addFile={addFile} removeFile={removeFile} label={'<span class="filepond--label-action"> Upload File(s) </span>'} />
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={tabIndex} index={1} keepMounted>
            <FileList
              toast={props.toast}
              files={files.filter(
                (file) =>
                  !(file?.creditCardTransactionAssignments?.length > 0 || file?.expenseReportAssignments?.length > 0) || props?.selectedFile?.uniqueId === file?.uniqueId
              )}
              updateFile={updateFile}
              deleteFile={deleteFile}
              setSelectedFile={props.fileToReturn ? setSelectedFile : null}
              selectedFileID={selectedFile}
              deleteMode={deleteMode}
              showFile={previewFile}
            />
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={tabIndex} index={2}>
            <FileList
              toast={props.toast}
              files={files}
              updateFile={updateFile}
              deleteFile={deleteFile}
              setSelectedFile={props.fileToReturn ? setSelectedFile : null}
              selectedFileID={selectedFile}
              showFile={previewFile}
            />
          </TabPanel>
        </DialogContent>
        <DialogActions>
          {deleteMode && tabIndex === 1 ? (
            <div>
              {deletedFiles.length > 0 ? (
                <Button onClick={undoDelete} color='primary'>
                  Undo
                </Button>
              ) : null}
              <Button onClick={finishDelete} color='primary'>
                Done
              </Button>
            </div>
          ) : (
            <div>
              <Button onClick={handleCancel} color='primary'>
                Close
              </Button>
              {tabIndex !== 1 ? null : (
                <Button onClick={() => setDeleteMode(true)} color='secondary'>
                  Delete
                </Button>
              )}
              <Button onClick={handleSave} color='primary'>
                Save
              </Button>
            </div>
          )}
        </DialogActions>
      </Dialog>
      <FilePreviewDialog file={files.find((file) => file.id === previewFileId)} open={previewFileId ? true : false} close={() => history.goBack()} toast={props.toast} />
    </>
  );
}
